import React, { useState, useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { Button } from '../../components/base/Button';
import { updateSEO, seoData } from '../../utils/seo';

const SuppliersPage: React.FC = () => {
  useEffect(() => {
    updateSEO(seoData.suppliers);
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    country: '',
    whatsappNumber: '',
    email: '',
    supplyCapacity: '',
    productPhotos: null as File | null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, productPhotos: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('fullName', formData.fullName);
      formDataToSubmit.append('country', formData.country);
      formDataToSubmit.append('whatsappNumber', formData.whatsappNumber);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('supplyCapacity', formData.supplyCapacity);
      if (formData.productPhotos) {
        formDataToSubmit.append('productPhotos', 'Uncollectable');
      }

      const response = await fetch('https://readdy.ai/api/form/d45j63vgaed3g6l52100', {
        method: 'POST',
        body: formDataToSubmit,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          country: '',
          whatsappNumber: '',
          email: '',
          supplyCapacity: '',
          productPhotos: null,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(8, 145, 178, 0.8)), url('https://public.readdy.ai/ai/img_res/4380822ce9183ad2b2e978861d35e344.jpg')`
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Partner with <strong>Henatos</strong>
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Join Africa's most trusted hides and skins network with reliable partnerships and steady income
          </p>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Why Partner with <strong>Henatos?</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer comprehensive support and reliable partnerships that help suppliers grow their business
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Advance Deposits</h3>
              <p className="text-gray-600">
                Receive advance payments to support your operations and ensure steady cash flow 
                for your business growth.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-repeat-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Steady Offtake</h3>
              <p className="text-gray-600">
                Guaranteed purchase agreements ensuring consistent demand for your quality 
                air-dried cow hides.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Technical Support</h3>
              <p className="text-gray-600">
                Comprehensive training on proper drying techniques, storage methods, and 
                quality standards to maximize your returns.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Quality Assurance</h3>
              <p className="text-gray-600">
                Clear quality guidelines and inspection processes to ensure your products 
                meet export standards consistently.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-truck-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Logistics Support</h3>
              <p className="text-gray-600">
                Coordinated collection and transportation services to streamline your 
                supply chain operations.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-handshake-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-4">Long-term Partnership</h3>
              <p className="text-gray-600">
                Build lasting business relationships with transparent terms and mutual 
                growth opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Supplier <strong>Requirements</strong>
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                Henatos partners with trusted vendors across Africa who meet our strict quality standards. 
                We provide comprehensive support to help you succeed in our network.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Air-Dried Only</h4>
                    <p className="text-gray-600">Must deliver only air-dried, unsalted hides meeting our moisture standards</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Quality Compliance</h4>
                    <p className="text-gray-600">Adherence to weight specifications (6.5kg-12kg) and damage-free standards</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Consistent Supply</h4>
                    <p className="text-gray-600">Ability to maintain regular supply volumes as per agreement terms</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-close-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">Rejection Policy</h4>
                    <p className="text-gray-600">Rejected or unfit hides must be collected within 15 days at supplier's expense</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Quality%20control%20inspection%20of%20air-dried%20cow%20hides%2C%20professional%20grading%20process%2C%20workers%20examining%20hides%20for%20moisture%20content%20and%20damage%2C%20quality%20assurance%20standards%20in%20African%20commodity%20trading%20facility&width=600&height=400&seq=requirements1&orientation=landscape"
                alt="Quality requirements for suppliers"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Supplier <strong>Application</strong>
            </h2>
            <p className="text-xl text-gray-600">
              Join our network of trusted suppliers across East and Southern Africa
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-8">
            <form onSubmit={handleSubmit} data-readdy-form id="supplier-application">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select your country</option>
                    <option value="Tanzania">Tanzania</option>
                    <option value="Burundi">Burundi</option>
                    <option value="Rwanda">Rwanda</option>
                    <option value="Zambia">Zambia</option>
                    <option value="Zimbabwe">Zimbabwe</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Number *
                  </label>
                  <input
                    type="tel"
                    id="whatsappNumber"
                    name="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="+255 123 456 789"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <label htmlFor="supplyCapacity" className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Supply Capacity (in kg) *
                </label>
                <input
                  type="text"
                  id="supplyCapacity"
                  name="supplyCapacity"
                  value={formData.supplyCapacity}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="e.g., 1000-2000 kg per month"
                />
              </div>
              
              <div className="mt-6">
                <label htmlFor="productPhotos" className="block text-sm font-medium text-gray-700 mb-2">
                  Product Photos
                </label>
                <input
                  type="file"
                  id="productPhotos"
                  name="productPhotos"
                  onChange={handleFileChange}
                  accept="image/*"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Upload photos of your hides (optional)</p>
              </div>
              
              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Thank you for your application! We will review your submission and contact you within 48 hours.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  There was an error submitting your application. Please try again or contact us directly.
                </div>
              )}
              
              <div className="mt-8">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Questions About <strong>Partnership?</strong>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Our team is ready to discuss partnership opportunities and answer any questions 
            about joining the Henatos supplier network.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="mailto:accounts@henatosbiz.com" variant="primary" size="lg">
              Email Us
            </Button>
            <Button href="https://wa.me/message" variant="secondary" size="lg">
              WhatsApp
            </Button>
            <Button href="/contact" variant="outline" size="lg">
              Contact Page
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SuppliersPage;
